import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { 
  CreditCard as CreditCardIcon, 
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import paymentService, { PaymentTransaction, SnapCallbackResult } from '../services/paymentService';
import { InvoiceItem } from '../services/invoiceService';

interface PaymentButtonProps {
  invoice: InvoiceItem;
  onPaymentUpdate?: (transaction: PaymentTransaction) => void;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'contained' | 'outlined';
  fullWidth?: boolean;
}

const PaymentButton: React.FC<PaymentButtonProps> = ({
  invoice,
  onPaymentUpdate,
  className = '',
  size = 'medium',
  variant = 'contained',
  fullWidth = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentTransaction, setPaymentTransaction] = useState<PaymentTransaction | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  // Check existing payment transaction on component mount
  useEffect(() => {
    checkExistingPayment();
  }, [invoice.id]);

  const checkExistingPayment = async () => {
    try {
      setIsLoadingStatus(true);
      const response = await paymentService.getPaymentByInvoice(invoice.id);
      
      if (response.success && response.payment_transaction) {
        setPaymentTransaction(response.payment_transaction);
      }
    } catch (error) {
      console.error('Failed to check existing payment:', error);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const handlePayment = async () => {
    try {
      setIsLoading(true);

      // Check if there's already a pending payment
      if (paymentTransaction?.is_pending) {
        // Resume existing payment
        await processExistingPayment(paymentTransaction);
        return;
      }

      // Create new payment
      const response = await paymentService.initiatePayment({
        invoice_id: invoice.id,
        return_url: `${window.location.origin}/payment/success`
      });

      if (response.success && response.snap_token && response.payment_transaction) {
        setPaymentTransaction(response.payment_transaction);
        
        // Show toast notification
        toast.success(t('payment.toast.linkCreated'));
        
        // Process payment with Snap
        await processPayment(response.snap_token, response.payment_transaction);
      } else {
        throw new Error(response.message || t('payment.button.initiateFailed'));
      }
    } catch (error: any) {
      console.error('Payment initiation failed:', error);
      toast.error(error.message || t('payment.toast.initiateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const processExistingPayment = async (transaction: PaymentTransaction) => {
    try {
      if (transaction.snap_token) {
        toast.info(t('payment.toast.resuming'));
        await processPayment(transaction.snap_token, transaction);
      } else {
        throw new Error(t('payment.toast.tokenUnavailable'));
      }
    } catch (error: any) {
      console.error('Failed to resume payment:', error);
      toast.error(error.message || t('payment.toast.resumeFailed'));
    }
  };

  const processPayment = async (snapToken: string, transaction: PaymentTransaction) => {
    try {
      await paymentService.processPayment(
        snapToken,
        // onSuccess
        (result: SnapCallbackResult) => {
          console.log('Payment successful:', result);
          toast.success(t('payment.toast.success'));
          navigate(`/payment/success?order_id=${result.order_id}&transaction_id=${result.transaction_id}`);
        },
        // onPending
        (result: SnapCallbackResult) => {
          console.log('Payment pending:', result);
          toast.info(t('payment.toast.processing'));
          navigate(`/payment/pending?order_id=${result.order_id}&transaction_id=${result.transaction_id}`);
        },
        // onError
        (result: SnapCallbackResult) => {
          console.error('Payment error:', result);
          toast.error(t('payment.toast.failed'));
          navigate(`/payment/error?order_id=${result.order_id}&transaction_id=${result.transaction_id}`);
        },
        // onClose
        () => {
          console.log('Payment popup closed');
          // Refresh payment status
          setTimeout(() => {
            checkExistingPayment();
          }, 1000);
        }
      );
    } catch (error: any) {
      console.error('Payment processing failed:', error);
      toast.error(error.message || t('payment.toast.processFailed'));
    }
  };

  const handleCheckPaymentStatus = async () => {
    if (!paymentTransaction) return;

    try {
      setIsLoading(true);
      const response = await paymentService.getPaymentStatus(paymentTransaction.id);
      
      if (response.success && response.payment_transaction) {
        setPaymentTransaction(response.payment_transaction);
        
        if (onPaymentUpdate) {
          onPaymentUpdate(response.payment_transaction);
        }

        // Show status update
        const status = response.payment_transaction.status;
        const statusText = paymentService.getPaymentStatusText(status);
        
        if (status === 'SUCCESS') {
          toast.success(t('payment.toast.status', { status: statusText }));
        } else if (status === 'FAILED' || status === 'EXPIRED' || status === 'CANCELLED') {
          toast.error(t('payment.toast.status', { status: statusText }));
        } else {
          toast.info(t('payment.toast.status', { status: statusText }));
        }
      }
    } catch (error: any) {
      console.error('Failed to check payment status:', error);
      toast.error(error.message || t('payment.toast.checkStatusFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Determine if invoice can accept payment
  const canAcceptPayment = paymentService.canInvoiceAcceptPayment(invoice.status, true);

  // If loading status, show skeleton
  if (isLoadingStatus) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    );
  }

  // If invoice cannot accept payment, show disabled state
  if (!canAcceptPayment) {
    return (
      <Button
        disabled
        variant="outlined"
        size={size}
        fullWidth={fullWidth}
        className={`${className} text-gray-400`}
        startIcon={<CreditCardIcon />}
      >
        {t('payment.button.notAvailable')}
      </Button>
    );
  }

  // If there's an existing successful payment
  if (paymentTransaction?.is_successful) {
    return (
      <Button
        disabled
        variant="outlined"
        size={size}
        fullWidth={fullWidth}
        className={`${className} text-green-600 border-green-600`}
        startIcon={<CheckCircleIcon />}
      >
        {t('payment.button.alreadyPaid')}
      </Button>
    );
  }

  // If there's an existing failed/expired payment
  if (paymentTransaction?.is_failed) {
    const statusText = paymentService.getPaymentStatusText(paymentTransaction.status);
    const IconComponent = paymentTransaction.status === 'EXPIRED' ? ScheduleIcon : 
                          paymentTransaction.status === 'CANCELLED' ? CancelIcon : ErrorIcon;
    
    return (
      <div className={`${className} space-y-2`}>
        <Button
          disabled
          variant="outlined"
          size={size}
          fullWidth={fullWidth}
          className="text-red-600 border-red-600"
          startIcon={<IconComponent />}
        >
          {statusText}
        </Button>
        
        <Button
          onClick={handlePayment}
          disabled={isLoading}
          variant="contained"
          color="primary"
          size={size}
          fullWidth={fullWidth}
          startIcon={<PaymentIcon />}
        >
          {isLoading ? t('payment.button.processing') : t('payment.button.tryAgain')}
        </Button>
      </div>
    );
  }

  // If there's a pending payment
  if (paymentTransaction?.is_pending) {
    return (
      <div className={`${className} space-y-2`}>
        <div className="flex items-center gap-2 text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
          <ScheduleIcon className="w-4 h-4" />
          <span>
            {t('payment.pending')}
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={() => processExistingPayment(paymentTransaction)}
            disabled={isLoading}
            variant="contained"
            color="primary"
            size={size}
            startIcon={<PaymentIcon />}
          >
            {isLoading ? t('payment.button.processing') : t('payment.button.continue')}
          </Button>
          
          <Button
            onClick={handleCheckPaymentStatus}
            disabled={isLoading}
            variant="outlined"
            size={size}
          >
            {t('payment.button.checkStatus')}
          </Button>
        </div>
      </div>
    );
  }

  // Default state - show pay button
  return (
    <Button
      onClick={handlePayment}
      disabled={isLoading}
      variant={variant}
      color="primary"
      size={size}
      fullWidth={fullWidth}
      className={className}
      startIcon={<PaymentIcon />}
    >
      {isLoading ? t('payment.button.processing') : t('payment.button.payNow')}
    </Button>
  );
};

export default PaymentButton;